import { useEffect, useState } from "react";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	InputAdornment,
	List,
	ListItem,
	ListItemText,
	ListItemIcon,
	Menu,
	MenuItem,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from "@mui/material";
import { ContentCopy, DeleteOutline, FileDownload, Link as LinkIcon, PictureAsPdf, TableChart } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import api from "../services/api";
import { formatDateTime, formatDate } from "../utils/dateUtils";

// A suite run's report: download as PDF (with failure screenshots) or CSV, or share a
// read-only link that expires (Suite Runs → Export). Backend: ReportExportService, ReportShareService.

const saveBlob = (blob, fileName) => {
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = fileName;
	document.body.appendChild(a);
	a.click();
	a.remove();
	setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const fileNameFrom = (disposition, fallback) => {
	const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition || "");
	return match ? decodeURIComponent(match[1]) : fallback;
};

function ShareDialog({ run, open, onClose }) {
	const { enqueueSnackbar } = useSnackbar();
	const [days, setDays] = useState(7);
	const [links, setLinks] = useState([]);
	const [created, setCreated] = useState(null);
	const [loading, setLoading] = useState(false);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState(null);

	useEffect(() => {
		if (!open) return;
		setCreated(null);
		setError(null);
		setLoading(true);
		api.get(`/suites/executions/${run.id}/shares`)
			.then((r) => setLinks(r.data))
			.catch((err) => setError(err.response?.data?.message || "Couldn't load the links"))
			.finally(() => setLoading(false));
	}, [open, run.id]);

	const create = async () => {
		setBusy(true);
		setError(null);
		try {
			const { data } = await api.post(`/suites/executions/${run.id}/shares`, { days });
			setCreated(data);
			setLinks((l) => [data, ...l]);
		} catch (err) {
			setError(err.response?.data?.message || "Couldn't create the link");
		} finally {
			setBusy(false);
		}
	};

	const revoke = async (id) => {
		try {
			await api.delete(`/suites/executions/shares/${id}`);
			setLinks((l) => l.filter((x) => x.id !== id));
			if (created?.id === id) setCreated(null);
			enqueueSnackbar("Link revoked", { variant: "success" });
		} catch (err) {
			enqueueSnackbar(err.response?.data?.message || "Couldn't revoke the link", { variant: "error" });
		}
	};

	const copy = async (text) => {
		try {
			await navigator.clipboard.writeText(text);
			enqueueSnackbar("Link copied", { variant: "success" });
		} catch {
			enqueueSnackbar("Couldn't copy: select the link and copy it", { variant: "warning" });
		}
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>Share this report</DialogTitle>
			<DialogContent dividers>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
					Anyone with the link can read this run's report, without an account: the results, errors and the screenshots of failed
					steps. Screenshots can show what was typed on the page. The link stops working when it expires or you revoke it.
				</Typography>
				{error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}
				<Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
					<Typography variant="body2">Expires after</Typography>
					<ToggleButtonGroup size="small" exclusive value={days} onChange={(_, v) => v && setDays(v)}>
						<ToggleButton value={7}>7 days</ToggleButton>
						<ToggleButton value={30}>30 days</ToggleButton>
					</ToggleButtonGroup>
					<Button variant="contained" onClick={create} disabled={busy} startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <LinkIcon />}>
						Create link
					</Button>
				</Box>
				{created && (
					<Alert severity="success" sx={{ mb: 2 }}>
						<Typography variant="body2" sx={{ mb: 1 }}>
							Copy the link now: it isn't shown again.
						</Typography>
						<TextField
							fullWidth
							size="small"
							value={created.url}
							InputProps={{
								readOnly: true,
								endAdornment: (
									<InputAdornment position="end">
										<IconButton size="small" onClick={() => copy(created.url)} aria-label="Copy link">
											<ContentCopy fontSize="small" />
										</IconButton>
									</InputAdornment>
								),
							}}
							onFocus={(e) => e.target.select()}
						/>
					</Alert>
				)}
				<Typography variant="subtitle2" sx={{ mt: 1 }}>
					Active links
				</Typography>
				{loading ? (
					<CircularProgress size={20} sx={{ mt: 1 }} />
				) : links.length === 0 ? (
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
						None.
					</Typography>
				) : (
					<List dense disablePadding>
						{links.map((l) => (
							<ListItem
								key={l.id}
								disableGutters
								secondaryAction={
									<Tooltip title="Revoke: the link stops working">
										<IconButton edge="end" size="small" onClick={() => revoke(l.id)} aria-label="Revoke link">
											<DeleteOutline fontSize="small" />
										</IconButton>
									</Tooltip>
								}
							>
								<ListItemText
									primary={`Expires ${formatDate(l.expiresAt)}`}
									secondary={[
										`Created ${formatDateTime(l.createdAt)}${l.createdByName ? ` by ${l.createdByName}` : ""}`,
										l.lastViewedAt ? `last opened ${formatDateTime(l.lastViewedAt)}` : "not opened yet",
									].join(" · ")}
								/>
							</ListItem>
						))}
					</List>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Close</Button>
			</DialogActions>
		</Dialog>
	);
}

export default function ReportExportMenu({ run }) {
	const { enqueueSnackbar } = useSnackbar();
	const [anchor, setAnchor] = useState(null);
	const [downloading, setDownloading] = useState(null);
	const [shareOpen, setShareOpen] = useState(false);
	const canShare = run.canStop !== false;

	const download = async (format) => {
		setAnchor(null);
		setDownloading(format);
		try {
			const response = await api.get(`/suites/executions/${run.id}/export`, { params: { format }, responseType: "blob" });
			saveBlob(response.data, fileNameFrom(response.headers["content-disposition"], `report.${format}`));
		} catch {
			enqueueSnackbar(`Couldn't create the ${format.toUpperCase()}`, { variant: "error" });
		} finally {
			setDownloading(null);
		}
	};

	return (
		<>
			<Button
				variant="outlined"
				size="small"
				startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <FileDownload />}
				disabled={!!downloading}
				onClick={(e) => setAnchor(e.currentTarget)}
			>
				{downloading ? "Preparing…" : "Export"}
			</Button>
			<Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
				<MenuItem onClick={() => download("pdf")}>
					<ListItemIcon>
						<PictureAsPdf fontSize="small" />
					</ListItemIcon>
					Download PDF
				</MenuItem>
				<MenuItem onClick={() => download("csv")}>
					<ListItemIcon>
						<TableChart fontSize="small" />
					</ListItemIcon>
					Download CSV
				</MenuItem>
				{canShare && (
					<MenuItem
						onClick={() => {
							setAnchor(null);
							setShareOpen(true);
						}}
					>
						<ListItemIcon>
							<LinkIcon fontSize="small" />
						</ListItemIcon>
						Share link…
					</MenuItem>
				)}
			</Menu>
			{canShare && <ShareDialog run={run} open={shareOpen} onClose={() => setShareOpen(false)} />}
		</>
	);
}
